"use client"

import { useState, useEffect } from "react"
import { getDatabase, ref, get, set, remove } from "firebase/database"
import { createUserWithEmailAndPassword } from "firebase/auth"
import { auth } from "../../Firebase/firestore"
import { Edit, Trash2, Plus, Check, X, Eye, EyeOff } from "lucide-react"
import "./supplier-management.css"

const SupplierManagement = () => {
  const [suppliers, setSuppliers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isAddingSupplier, setIsAddingSupplier] = useState(false)
  const [isEditingSupplier, setIsEditingSupplier] = useState(null)
  const [newSupplier, setNewSupplier] = useState({
    brand: "",
    email: "",
    password: "",
    contactPerson: "",
    phone: "",
    address: "",
    status: "active",
  })
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  // Fetch suppliers
  useEffect(() => {
    const fetchSuppliers = async () => {
      setIsLoading(true)
      try {
        const db = getDatabase()
        const suppliersRef = ref(db, "suppliers")
        const snapshot = await get(suppliersRef)

        if (snapshot.exists()) {
          const suppliersData = snapshot.val()
          const suppliersList = Object.keys(suppliersData).map((key) => ({
            id: key,
            ...suppliersData[key],
          }))

          setSuppliers(suppliersList)
        } else {
          setSuppliers([])
        }
        setError("") // Clear any previous errors
      } catch (error) {
        console.error("Error fetching suppliers:", error)
        setError("Failed to load suppliers. Please try again.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchSuppliers()
  }, [])

  // Handle input change for new supplier
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setNewSupplier((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  // Add new supplier
  const handleAddSupplier = async () => {
    setError("")
    setSuccess("")

    // Validate inputs
    if (!newSupplier.brand || !newSupplier.email || !newSupplier.password) {
      setError("Brand, email, and password are required.")
      return
    }

    try {
      setIsLoading(true)

      // Check if email already exists in suppliers
      const emailExists = suppliers.some((supplier) => supplier.email.toLowerCase() === newSupplier.email.toLowerCase())

      if (emailExists) {
        setError("A supplier with this email already exists.")
        setIsLoading(false)
        return
      }

      // Create user in Firebase Auth with error handling
      let userCredential
      try {
        userCredential = await createUserWithEmailAndPassword(auth, newSupplier.email, newSupplier.password)
      } catch (authError) {
        console.error("Firebase Auth error:", authError)
        setError(authError.message || "Authentication failed. Please try again.")
        setIsLoading(false)
        return
      }

      const userId = userCredential.user.uid

      // Add supplier to database
      const db = getDatabase()
      const suppliersRef = ref(db, `suppliers/${userId}`)

      // Create supplier object without password
      const supplierData = {
        brand: newSupplier.brand,
        email: newSupplier.email,
        contactPerson: newSupplier.contactPerson || "",
        phone: newSupplier.phone || "",
        address: newSupplier.address || "",
        status: newSupplier.status || "active",
        createdAt: new Date().toISOString(),
      }

      try {
        await set(suppliersRef, supplierData)
      } catch (dbError) {
        console.error("Database write error:", dbError)
        setError("Failed to save supplier data. Please try again.")
        setIsLoading(false)
        return
      }

      // Add to brand mapping for login
      try {
        const brandMappingRef = ref(db, `brandMapping/${newSupplier.email.toLowerCase()}`)
        await set(brandMappingRef, newSupplier.brand)
      } catch (mappingError) {
        console.error("Brand mapping error:", mappingError)
        // Continue even if this fails
      }

      // Reset form and update UI
      setNewSupplier({
        brand: "",
        email: "",
        password: "",
        contactPerson: "",
        phone: "",
        address: "",
        status: "active",
      })

      setIsAddingSupplier(false)
      setSuccess("Supplier added successfully!")

      // Add to local state
      setSuppliers((prev) => [...prev, { id: userId, ...supplierData }])
    } catch (error) {
      console.error("Error adding supplier:", error)
      setError(error.message || "Failed to add supplier. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  // Edit supplier
  const handleEditSupplier = (supplier) => {
    setIsEditingSupplier(supplier.id)
    setNewSupplier({
      brand: supplier.brand,
      email: supplier.email,
      password: "",
      contactPerson: supplier.contactPerson || "",
      phone: supplier.phone || "",
      address: supplier.address || "",
      status: supplier.status || "active",
    })
  }

  // Update supplier
  const handleUpdateSupplier = async () => {
    setError("")
    setSuccess("")

    if (!newSupplier.brand || !newSupplier.email) {
      setError("Brand and email are required.")
      return
    }

    try {
      setIsLoading(true)

      const db = getDatabase()
      const supplierRef = ref(db, `suppliers/${isEditingSupplier}`)

      // Update supplier data
      const supplierData = {
        brand: newSupplier.brand,
        email: newSupplier.email,
        contactPerson: newSupplier.contactPerson || "",
        phone: newSupplier.phone || "",
        address: newSupplier.address || "",
        status: newSupplier.status || "active",
        updatedAt: new Date().toISOString(),
      }

      await set(supplierRef, supplierData)

      // Update brand mapping if email changed
      const oldSupplier = suppliers.find((s) => s.id === isEditingSupplier)
      if (oldSupplier && oldSupplier.email !== newSupplier.email) {
        // Remove old mapping
        const oldMappingRef = ref(db, `brandMapping/${oldSupplier.email.toLowerCase()}`)
        await remove(oldMappingRef)

        // Add new mapping
        const newMappingRef = ref(db, `brandMapping/${newSupplier.email.toLowerCase()}`)
        await set(newMappingRef, newSupplier.brand)
      }

      // Reset form and update UI
      setNewSupplier({
        brand: "",
        email: "",
        password: "",
        contactPerson: "",
        phone: "",
        address: "",
        status: "active",
      })

      setIsEditingSupplier(null)
      setSuccess("Supplier updated successfully!")

      // Update local state
      setSuppliers((prev) =>
        prev.map((s) => (s.id === isEditingSupplier ? { id: isEditingSupplier, ...supplierData } : s)),
      )
    } catch (error) {
      console.error("Error updating supplier:", error)
      setError(error.message || "Failed to update supplier. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  // Delete supplier
  const handleDeleteSupplier = async (supplierId, email) => {
    if (!window.confirm("Are you sure you want to delete this supplier?")) {
      return
    }

    try {
      setIsLoading(true)

      const db = getDatabase()

      // Remove supplier from database
      const supplierRef = ref(db, `suppliers/${supplierId}`)
      await remove(supplierRef)

      // Remove brand mapping
      const mappingRef = ref(db, `brandMapping/${email.toLowerCase()}`)
      await remove(mappingRef)

      // Update UI
      setSuccess("Supplier deleted successfully!")
      setSuppliers((prev) => prev.filter((s) => s.id !== supplierId))
    } catch (error) {
      console.error("Error deleting supplier:", error)
      setError(error.message || "Failed to delete supplier. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  // Cancel form
  const handleCancel = () => {
    setIsAddingSupplier(false)
    setIsEditingSupplier(null)
    setNewSupplier({
      brand: "",
      email: "",
      password: "",
      contactPerson: "",
      phone: "",
      address: "",
      status: "active",
    })
    setError("")
  }

  return (
    <div className="supplier-management-container">
      <div className="supplier-management-header">
        <h1>Supplier Management</h1>
        <button
          className="add-supplier-button"
          onClick={() => setIsAddingSupplier(true)}
          disabled={isAddingSupplier || isEditingSupplier}
        >
          <Plus size={16} /> Add Supplier
        </button>
      </div>

      {error && <div className="supplier-error-message">{error}</div>}
      {success && <div className="supplier-success-message">{success}</div>}

      {/* Add/Edit Supplier Form */}
      {(isAddingSupplier || isEditingSupplier) && (
        <div className="supplier-form-container">
          <h2>{isEditingSupplier ? "Edit Supplier" : "Add New Supplier"}</h2>
          <div className="supplier-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="brand">
                  Brand Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="brand"
                  name="brand"
                  value={newSupplier.brand}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={newSupplier.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {!isEditingSupplier && (
              <div className="form-group password-group">
                <label htmlFor="password">
                  Password <span className="required">*</span>
                </label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={newSupplier.password}
                    onChange={handleInputChange}
                    required={!isEditingSupplier}
                  />
                  <button type="button" className="toggle-password" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contactPerson">Contact Person</label>
                <input
                  type="text"
                  id="contactPerson"
                  name="contactPerson"
                  value={newSupplier.contactPerson}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input type="text" id="phone" name="phone" value={newSupplier.phone} onChange={handleInputChange} />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address">Address</label>
              <textarea id="address" name="address" value={newSupplier.address} onChange={handleInputChange} rows={3} />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select id="status" name="status" value={newSupplier.status} onChange={handleInputChange}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-button" onClick={handleCancel}>
                <X size={16} /> Cancel
              </button>
              <button
                type="button"
                className="save-button"
                onClick={isEditingSupplier ? handleUpdateSupplier : handleAddSupplier}
              >
                <Check size={16} /> {isEditingSupplier ? "Update Supplier" : "Add Supplier"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suppliers List */}
      <div className="suppliers-list-container">
        {isLoading && !isAddingSupplier && !isEditingSupplier ? (
          <div className="loading-spinner">Loading suppliers...</div>
        ) : suppliers.length === 0 ? (
          <div className="no-suppliers">No suppliers found. Add your first supplier.</div>
        ) : (
          <div className="suppliers-table-container">
            <table className="suppliers-table">
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Email</th>
                  <th>Contact Person</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td>{supplier.brand}</td>
                    <td>{supplier.email}</td>
                    <td>{supplier.contactPerson || "-"}</td>
                    <td>{supplier.phone || "-"}</td>
                    <td>
                      <span className={`status-badge ${supplier.status}`}>{supplier.status}</span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="edit-button"
                          onClick={() => handleEditSupplier(supplier)}
                          disabled={isAddingSupplier || isEditingSupplier}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDeleteSupplier(supplier.id, supplier.email)}
                          disabled={isAddingSupplier || isEditingSupplier}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default SupplierManagement;

